/**
 * Component: UserAgeStatistic
 * Horizontal stacked bar for user age ranges with inline legends.
 */

import { Fragment } from "react";
import { CustomerActiveIcon, ExternalIcon } from "@/assets/svg";
import {
  BarGraph,
  type BarGraphSegment,
} from "@/components/ui/bar-graph/bar-graph";
import { userAgeSegmentColors } from "../../../../mock";
import type { AgeRangeData } from "../../../../domain/model/DashboardModel";

export interface UserAgeStatisticProps {
  data: AgeRangeData[];
}

export const UserAgeStatistic = ({ data }: UserAgeStatisticProps): React.JSX.Element => {
  if (data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Age Statistic</h3>
        </div>
        <div className="flex items-center justify-center h-[80px] text-gray-500">
          No age data available
        </div>
      </div>
    );
  }

  const barSegments: ReadonlyArray<BarGraphSegment> = data.map(
    (segment, index) => ({
      label: segment.range,
      value: segment.count,
      color:
        userAgeSegmentColors[index] ??
        userAgeSegmentColors[userAgeSegmentColors.length - 1],
    }),
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-[0_10px_15px_-3px_rgba(15,23,42,0.05),0_4px_6px_-4px_rgba(15,23,42,0.03)]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Age Statistic</h3>
        <button
          className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center hover:opacity-80 focus:outline-none active:opacity-60"
          aria-label="Open external link"
          type="button"
        >
          <ExternalIcon />
        </button>
      </div>
      <div className="py-1 pb-2">
        <BarGraph
          segments={barSegments}
          ariaLabel="User age distribution bar graph"
          className="h-8 w-full"
        />
      </div>
      <div className="flex justify-between items-center w-full mt-2 text-xs gap-2">
        {data.map((segment, index) => (
          <Fragment key={segment.range}>
            {index > 0 && (
              <div className="w-px h-5 bg-gray-200" />
            )}
            <div className="flex items-center gap-2">
              <div className="font-normal text-sm leading-5">{segment.range}</div>
              <div className="flex items-center gap-1">
                <span className="font-bold text-sm leading-5">
                  {segment.count.toLocaleString()}
                </span>
                <span className="inline-flex items-center justify-center [&_svg]:w-[14px] [&_svg]:h-[14px]">
                  <CustomerActiveIcon />
                </span>
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
};
